import { useCallback, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  AssertionBadge,
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  StatusBadge,
} from '@/components/ui'
import { ScoreMeter } from '@/components/charts/Contributions'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { dateTime, matchTypeLabel, relativeTime, sentence } from '@/lib/format'
import type { Ack, ContactDetail, Match, Page, ProfileAttribute } from '@/types'

const LIST_FIELDS: Array<{ key: keyof NonNullable<ContactDetail['profile']>; label: string }> = [
  { key: 'looking_for', label: 'Looking for' },
  { key: 'can_offer', label: 'Can offer' },
  { key: 'services', label: 'Services' },
  { key: 'products', label: 'Products' },
  { key: 'skills', label: 'Skills' },
  { key: 'expertise', label: 'Expertise' },
  { key: 'target_customers', label: 'Target customers' },
  { key: 'preferred_connections', label: 'Wants to meet' },
  { key: 'business_goals', label: 'Goals' },
  { key: 'collaboration_interests', label: 'Open to collaborating on' },
  { key: 'experience', label: 'Experience' },
  { key: 'interests', label: 'Interests' },
]

export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ tone: 'good' | 'critical'; text: string } | null>(null)

  const contact = useQuery(
    useCallback(() => api.get<ContactDetail>(`/contacts/${contactId}`), [contactId]),
    [contactId],
  )
  const matches = useQuery(
    useCallback(
      () => api.get<Page<Match>>('/matches', { contact_id: contactId, limit: 20 }),
      [contactId],
    ),
    [contactId],
  )

  async function act(label: string, run: () => Promise<Ack>) {
    setBusy(label)
    setNotice(null)
    try {
      const ack = await run()
      setNotice({ tone: 'good', text: ack.detail ?? 'Done.' })
      contact.reload()
    } catch (err) {
      setNotice({
        tone: 'critical',
        text: err instanceof ApiError ? err.message : 'That did not work.',
      })
    } finally {
      setBusy(null)
    }
  }

  if (contact.loading && !contact.data) return <Spinner label="Loading contact" />
  if (contact.error) return <ErrorNote message={contact.error} onRetry={contact.reload} />
  if (!contact.data) return null

  const person = contact.data
  const profile = person.profile
  const optedOut = person.status === 'opted_out'

  return (
    <>
      <PageHeader
        title={person.name}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={person.status} />
            <span className="tabular text-ink-muted">{person.phone_e164}</span>
            {profile?.headline && <span className="text-ink-muted">· {profile.headline}</span>}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {!optedOut && (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy !== null}
                onClick={() =>
                  act('outreach', () =>
                    api.post<Ack>(`/contacts/${person.id}/outreach`, { force: false }),
                  )
                }
              >
                {busy === 'outreach' ? 'Queueing…' : 'Start outreach'}
              </button>
            )}
            {!optedOut && (
              <button
                type="button"
                className="btn-danger"
                disabled={busy !== null}
                onClick={() => {
                  if (!confirm(`Opt ${person.name} out permanently? This cannot be undone.`)) return
                  void act('optout', () =>
                    api.post<Ack>(`/contacts/${person.id}/opt-out`, {
                      reason: 'Admin action from the dashboard',
                    }),
                  )
                }}
              >
                Opt out
              </button>
            )}
          </div>
        }
      />

      {notice && (
        <p
          className="mb-4 rounded-md px-3 py-2 text-sm"
          role="status"
          style={{
            background: `color-mix(in srgb, var(--${notice.tone}) 12%, transparent)`,
            color: `var(--${notice.tone})`,
          }}
        >
          {notice.text}
        </p>
      )}

      {optedOut && (
        <div className="mb-4">
          <Badge tone="critical" icon="⊘">
            Opted out {relativeTime(person.opted_out_at)} — no further messages will be sent
          </Badge>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Card
            title="Profile"
            subtitle={
              profile
                ? `Version ${profile.version} · last extracted ${relativeTime(profile.last_extracted_at)}`
                : 'No structured profile yet'
            }
            action={
              profile && (
                <div className="text-right text-xs text-ink-muted">
                  <p>
                    <span className="tabular font-medium text-ink">
                      {Math.round(profile.opportunity_readiness * 100)}%
                    </span>{' '}
                    ready
                  </p>
                  <p className="text-ink-faint">
                    <span className="tabular">{Math.round(profile.completeness * 100)}%</span>{' '}
                    complete
                  </p>
                </div>
              )
            }
          >
            {!profile ? (
              <EmptyState
                title="Nothing extracted yet"
                hint="A profile appears once this person replies and the extraction agent has run."
              />
            ) : (
              <div className="space-y-4 px-4 py-4">
                {profile.summary && <p className="text-sm text-ink">{profile.summary}</p>}

                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                  <Fact label="Profession" value={profile.profession} />
                  <Fact label="Company" value={profile.company} />
                  <Fact label="Industry" value={profile.industry} />
                  <Fact label="Location" value={profile.location} />
                </dl>

                <div className="space-y-3 border-t border-line pt-3">
                  {LIST_FIELDS.map(({ key, label }) => {
                    const values = profile[key] as string[]
                    if (!Array.isArray(values) || values.length === 0) return null
                    return (
                      <div key={key}>
                        <p className="mb-1 text-xs font-medium text-ink-faint">{label}</p>
                        <ul className="flex flex-wrap gap-1.5">
                          {values.map((value) => (
                            <li
                              key={value}
                              className="rounded px-2 py-0.5 text-xs text-ink"
                              style={{ background: 'var(--surface-2)' }}
                            >
                              {value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </Card>

          <Card
            title="Evidence"
            subtitle="Every derived fact, with its confidence and the words it came from"
          >
            {person.attributes.length === 0 ? (
              <EmptyState title="No attributes recorded yet" />
            ) : (
              <ul className="divide-y divide-line">
                {person.attributes.map((attribute) => (
                  <AttributeRow key={attribute.id} attribute={attribute} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card title="Opportunities" subtitle="Discrete, actionable needs and offers">
            {person.opportunities.length === 0 ? (
              <EmptyState title="None captured yet" />
            ) : (
              <ul className="divide-y divide-line">
                {person.opportunities.map((opportunity) => (
                  <li key={opportunity.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-ink">{opportunity.title}</p>
                      <Badge tone={opportunity.kind === 'need' ? 'info' : 'neutral'}>
                        {opportunity.kind === 'need' ? 'Needs' : 'Offers'}
                      </Badge>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <AssertionBadge assertion={opportunity.assertion} />
                      <Badge tone={opportunity.urgency === 'urgent' ? 'warning' : 'neutral'}>
                        {sentence(opportunity.urgency)}
                      </Badge>
                      <span className="text-xs tabular text-ink-faint">
                        {Math.round(opportunity.confidence * 100)}% confident
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card
            title="Matches"
            subtitle={`${person.match_count} found for this person`}
          >
            {matches.loading && !matches.data ? (
              <Spinner />
            ) : !matches.data?.items.length ? (
              <EmptyState title="No matches yet" />
            ) : (
              <ul className="divide-y divide-line">
                {matches.data.items.map((match) => {
                  const other =
                    match.person_a.contact_id === person.id ? match.person_b : match.person_a
                  return (
                    <li key={match.id} className="px-4 py-3">
                      <Link
                        to={`/opportunities/${match.id}`}
                        className="flex items-center gap-2.5 hover:underline"
                      >
                        <Avatar name={other.name} size={28} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink">
                            {other.name}
                          </span>
                          <span className="block text-xs text-ink-muted">
                            {matchTypeLabel(match.match_type)}
                          </span>
                        </span>
                        <StatusBadge status={match.status} />
                      </Link>
                      <div className="mt-2">
                        <ScoreMeter score={match.score} size="sm" />
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </Card>

          <Card title="Activity">
            <dl className="space-y-2 px-4 py-4 text-sm">
              <Row label="Conversations" value={String(person.conversation_count)} />
              <Row label="Outreach attempts" value={String(person.outreach_attempts)} />
              <Row label="Last reply" value={relativeTime(person.last_inbound_at)} />
              <Row label="Last outreach" value={relativeTime(person.last_outreach_at)} />
              <Row label="Added" value={dateTime(person.created_at)} />
              <Row label="Source" value={sentence(person.source)} />
            </dl>
            {person.tags.length > 0 && (
              <div className="border-t border-line px-4 py-3">
                <ul className="flex flex-wrap gap-1.5">
                  {person.tags.map((tag) => (
                    <li key={tag}>
                      <Badge>{tag}</Badge>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="border-t border-line px-4 py-3">
              <Link
                to={`/conversations?contact_id=${person.id}`}
                className="text-xs text-ink-muted hover:text-ink"
              >
                View conversations →
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

function AttributeRow({ attribute }: { attribute: ProfileAttribute }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="px-4 py-2.5">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="min-w-0">
          <span className="text-xs text-ink-faint">{sentence(attribute.key)}</span>
          <span className="block truncate text-sm text-ink">{attribute.value}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <AssertionBadge assertion={attribute.assertion} />
          <span className="text-xs tabular text-ink-faint">
            {Math.round(attribute.confidence * 100)}%
          </span>
        </span>
      </button>

      {open && (
        <div className="mt-2 space-y-1 rounded-md px-3 py-2 text-xs" style={{ background: 'var(--surface-2)' }}>
          {attribute.source_quote ? (
            <p className="text-ink">
              <span className="text-ink-faint">They said: </span>“{attribute.source_quote}”
            </p>
          ) : (
            <p className="text-ink-muted">
              Inferred from the conversation — no direct quote supports this.
            </p>
          )}
          <p className="text-ink-faint">
            Confirmed {attribute.confirmations}×· first seen {relativeTime(attribute.first_seen_at)}
          </p>
          {attribute.conflicts_with_id && (
            <p style={{ color: 'var(--serious)' }}>
              ⚠ Conflicts with an earlier stated fact — needs a human decision.
            </p>
          )}
        </div>
      )}
    </li>
  )
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-ink-faint">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value || <span className="text-ink-faint">—</span>}</dd>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right text-ink">{value}</dd>
    </div>
  )
}
